import { getGPUTier, type TierResult } from 'detect-gpu';
import { parse as queryStringParse } from 'query-string';

// TIER0 : No WebGL or very low-end device
// TIER1: Low-range Device
// TIER2: Mid-range Device
// TIER3: High-range Device
const GPU_RANGE_TEXT = ['VERY-LOW', 'LOW', 'MEDIUM', 'HIGH'];
export const GPU_RANGE = {
    VERY_LOW: 0,
    LOW: 1,
    MEDIUM: 2,
    HIGH: 3,
};

// Singleton class to be import directly where it's needed
// await PerformanceProfile.ready

declare interface Profile {
    rtSamples: number;
    maxDpr: number;
}

let stats;
class PerformanceProfile {
    isReady: boolean;
    isDevModeReady: boolean;
    gpuTier!: TierResult;
    fps: number;
    debug: boolean;
    lastFrameTime: number;
    readyPromise: Promise<void>;
    devMode: boolean;
    globalProfile!: Profile;
    devModePromise!: Promise<void>;
    range: number;
    maxTests: number;
    fpsTests: number[];
    gpuMax!: number;
    gpuMin!: number;
    timeChecker: typeof performance | typeof Date;
    minFps: number;
    maxFps: number;
    frames: number;
    fpsArray: number[];
    currentFps: number;
    fpsInterval!: NodeJS.Timeout;
    currentTierElement!: HTMLElement;
    dprElement!: HTMLElement;
    beginTime!: number;
    GUI!: unknown;
    onRendererUpdateProfile?: () => void;
    onLayerManagerUpdateProfile?: () => void;

    constructor() {
        this.isReady = false;
        this.fps = 60;

        this.fpsArray = [];
        this.currentFps = 0;
        this.minFps = Infinity;
        this.maxFps = 0;

        this.range = 2;

        this.lastFrameTime = 0;
        this.frames = 0;
        this.isDevModeReady = false;
        this.readyPromise = new Promise<void>(this.init.bind(this));

        this.timeChecker = performance || Date;

        this.maxTests = 3;
        // How Many time the GPU can try a higher Performance Profile
        this.fpsTests = [];
        for (let i = 0; i < GPU_RANGE_TEXT.length; i++) {
            this.fpsTests.push(0);
        }

        // Enable FPS check and other Dev features ?performanceMode
        this.debug = Object.prototype.hasOwnProperty.call(queryStringParse(location.search), 'performanceMode');
        this.devMode = Object.prototype.hasOwnProperty.call(queryStringParse(location.search), 'webglDevMode');
        if (this.debug) this.addDebugMode();
        if (this.devMode) {
            this.devModePromise = new Promise<void>(this.addDevMode.bind(this));
        }

        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore-disable-next-line
        if (this.devMode) window.__performanceProfile = this;
    }

    async init(resolve) {
        const gpuInfo = localStorage.getItem('gpuInfo');
        if (!gpuInfo) {
            this.gpuTier = await getGPUTier();
            if (this.gpuTier.type == 'FALLBACK') {
                this.gpuTier.tier = 2;
            }
            localStorage.setItem('gpuInfo', JSON.stringify(this.gpuTier));
        } else {
            this.gpuTier = JSON.parse(gpuInfo);
        }

        this.gpuMax = GPU_RANGE[GPU_RANGE_TEXT[this.gpuTier.tier]];
        this.gpuMin = this.gpuMax === GPU_RANGE.HIGH ? GPU_RANGE.LOW : GPU_RANGE.VERY_LOW;
        this.range = this.gpuMax;
        this.isReady = true;

        this.globalProfile = {
            rtSamples: 3,
            maxDpr: 2,
        };

        this.setGlobalProfile();
        resolve();

        setTimeout(() => {
            this.checkAverageFps();
            this.fpsInterval = setInterval(this.checkAverageFps.bind(this), 5000);
        }, 5000);
    }

    async ready() {
        await this.readyPromise;
    }

    async devModeReady() {
        await this.devModePromise;
    }

    addDebugMode() {
        this.addFPSCheck();
        this.addDOMProfile();
    }

    setGlobalProfile() {
        if (this.range === GPU_RANGE.VERY_LOW) {
            this.globalProfile['rtSamples'] = 1;
            this.globalProfile['maxDpr'] = 1.25;
        } else if (this.range === GPU_RANGE.LOW) {
            this.globalProfile['rtSamples'] = 2;
            this.globalProfile['maxDpr'] = 1.5;
        } else if (this.range === GPU_RANGE.MEDIUM) {
            this.globalProfile.rtSamples = 3;
            this.globalProfile.maxDpr = 1.75;
        } else if (this.range === GPU_RANGE.HIGH) {
            this.globalProfile['rtSamples'] = 4;
            this.globalProfile['maxDpr'] = 2.0;
        }
    }

    updateProfile(upgradeNumber) {
        const lastRange = this.range;
        this.range += upgradeNumber;

        this.range = Math.min(this.range, this.gpuMax);
        this.range = Math.max(this.range, this.gpuMin);

        if (lastRange == this.range) return;

        if (this.range > lastRange) {
            if (this.fpsTests[this.range] >= this.maxTests) {
                if (this.debug) {
                    // eslint-disable-next-line no-console
                    console.log(
                        `Max FPS Test Attempt: ${GPU_RANGE_TEXT[this.range]} - Assign ${GPU_RANGE_TEXT[lastRange]}`,
                    );
                }
                this.range = lastRange;
                return;
            }
        }

        if (lastRange > this.range) {
            this.fpsTests[lastRange] = Math.min(this.maxTests, this.fpsTests[lastRange] + 1);
            if (this.debug) {
                // eslint-disable-next-line no-console
                console.log(`Test Number ${this.fpsTests[lastRange]} on Profile ${GPU_RANGE_TEXT[this.range]}`);
            }
        }

        this.setGlobalProfile();
        if (this.debug)
            // eslint-disable-next-line no-console
            console.log(
                `Update WebGL Performance Profile from ${GPU_RANGE_TEXT[lastRange]} to ${
                    GPU_RANGE_TEXT[this.range]
                }, Test Number:${this.fpsTests[this.range]}`,
            );

        if (this.debug) {
            this.dprElement.innerHTML = `<strong>DPR:</strong> ${this.globalProfile.maxDpr}`;
            this.currentTierElement.innerHTML = `<strong>Current Tier:</strong> ${GPU_RANGE_TEXT[this.range]}`;
        }

        this.onRendererUpdateProfile && this.onRendererUpdateProfile();
        this.onLayerManagerUpdateProfile && this.onLayerManagerUpdateProfile();
    }

    checkAverageFps() {
        let avgFPS = 0;
        for (let i = 0; i < this.fpsArray.length; i++) {
            avgFPS += this.fpsArray[i];
        }
        avgFPS /= this.fpsArray.length;

        if (avgFPS < 35) this.updateProfile(-2);
        if (avgFPS < 50) this.updateProfile(-1);
        else if (avgFPS > 58) this.updateProfile(1);

        this.fpsArray = [];
    }

    addFPSCheck() {
        // TODO Thunder: Find way to import debug only script and avoid typescript issue
        const script = document.createElement('script');
        script.onload = () => {
            //eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore-disable-next-line
            stats = new Stats();
            document.body.appendChild(stats.dom);

            stats.dom.style.top = 'initial';
            stats.dom.style.left = 'initial';
            stats.dom.style.bottom = '0px';
            stats.dom.style.right = '0px';
        };
        script.src = '//mrdoob.github.io/stats.js/build/stats.min.js';
        document.head.appendChild(script);
    }

    addDevMode(resolve) {
        // TODO Thunder: Find way to import debug only script and avoid typescript issue
        const script = document.createElement('script');
        script.onload = () => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-disable-next-line
            const pane = new Tweakpane.Pane();
            pane.containerElem_.style.position = 'fixed';
            pane.containerElem_.style.zIndex = '9999999';
            pane.containerElem_.style.width = '450px';

            this.GUI = pane.addFolder({
                title: '🛠 WebGL Config 🎨',
                expanded: true,
            });

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-disable-next-line
            this.GUI.floorGameUp = this.GUI.addFolder({
                title: 'Floor Game Up',
                expanded: false,
            });

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-disable-next-line
            this.GUI.floorGameDown = this.GUI.addFolder({
                title: 'Floor Game Down',
                expanded: false,
            });

            this.isDevModeReady = true;
            resolve();
        };
        script.src = 'https://cdn.jsdelivr.net/npm/tweakpane@3.1.0/dist/tweakpane.min.js';
        document.head.appendChild(script);
    }

    async addDOMProfile() {
        await this.ready();

        const container = document.createElement('div');
        container.style.bottom = '50px';
        container.style.right = '0';
        container.style.position = 'fixed';
        container.style.zIndex = '10000';
        container.style.background = '#000';
        container.style.border = '1px solid #fff';
        container.style.color = '#fff';
        container.style.display = 'flex';
        container.style.flexDirection = 'column';
        container.style.padding = '5px 20px';
        container.style.fontSize = '12px';
        container.style.lineHeight = '12px';

        const gpu = document.createElement('div');
        container.appendChild(gpu);
        const tier = document.createElement('div');
        container.appendChild(tier);
        this.currentTierElement = document.createElement('div');
        container.appendChild(this.currentTierElement);

        this.dprElement = document.createElement('div');
        container.appendChild(this.dprElement);

        gpu.innerHTML = `<strong>GPU:</strong> ${this.gpuTier.gpu}`;
        tier.innerHTML = `<strong>GPU Tier:</strong> ${GPU_RANGE_TEXT[this.gpuMax]}`;
        this.currentTierElement.innerHTML = `<strong>Current Tier:</strong> ${GPU_RANGE_TEXT[this.range]}`;
        this.dprElement.innerHTML = `<strong>DPR:</strong> ${this.globalProfile.maxDpr}`;

        document.body.appendChild(container);
    }

    startFPSCheck() {
        this.beginTime = this.timeChecker.now();

        if (!this.debug) return;
        if (stats) stats.begin();
    }

    endFPSCheck() {
        this.frames++;
        const time = this.timeChecker.now();

        if (time >= this.lastFrameTime + 1000) {
            // Check Frame per Second
            this.currentFps = Math.round((this.frames * 1000) / (time - this.lastFrameTime));

            this.lastFrameTime = time;
            this.frames = 0;

            this.minFps = Math.min(this.minFps, this.currentFps);
            this.maxFps = Math.max(this.maxFps, this.currentFps);

            this.fpsArray.push(this.currentFps);
        }

        if (!this.debug) return;
        if (stats) stats.end();
    }

    exportConfig(name, config) {
        const date = new Date();
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0'); //January is 0!
        const yyyy = date.getFullYear();
        const time = date.getHours() + 'H' + date.getMinutes();
        const dateString = mm + '-' + dd + '-' + yyyy;

        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([JSON.stringify(config, null, '\t')], { type: 'application/json' }));
        // eslint-disable-next-line no-console
        console.log(time);
        a.download = `${name}_${dateString}_${time}.json`;
        a.click();
    }
}

export default new PerformanceProfile();
